import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ExcelJS from 'exceljs';
import reportService from '../../services/reportService';
import { classificationService } from '../../services/classificationService';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import {
  filterProductCategoriesExcludingRecetaMirror,
  filterProductSubcategoriesExcludingRecetaMirror,
  filterProductFamiliesExcludingRecetaMirror,
} from '../../utils/productClassificationFilters';
import { getWarehouses } from '../../services/warehousesService';
import { authService } from '../../services/authService';
import { filterWarehousesForUser } from '../../utils/limitedUserAccess';
import Toast from '../../components/Common/Toast';

const STOCK_ZERO_EPS = 0.0001;

const hasNonZeroStock = (qty) => Math.abs(Number(qty) || 0) > STOCK_ZERO_EPS;

const formatStockQty = (qty) =>
  Number(qty).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

const stockQtyClassName = (qty) => {
  const n = Number(qty) || 0;
  if (n < -STOCK_ZERO_EPS) return 'text-red-600 font-semibold';
  if (n > STOCK_ZERO_EPS) return 'text-gray-900 font-medium';
  return 'text-gray-500';
};

const Kardex = () => {
  const navigate = useNavigate();
  const {
    produccionEnabled,
    produccionConcesionariaEnabled,
    restaurantesEnabled,
    machineryEnabled,
    loaded: moduleSettingsLoaded,
  } = useModuleSettings();
  const showRecipeSegmentation =
    moduleSettingsLoaded &&
    (produccionEnabled || produccionConcesionariaEnabled || restaurantesEnabled);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // Productos sin filtrar
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  
  // Refs para evitar llamadas duplicadas
  const hasLoadedReport = useRef(false);
  const hasLoadedFilters = useRef(false);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [selectedFamily, setSelectedFamily] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [selectedCondition, setSelectedCondition] = useState('');
  const [averageCost, setAverageCost] = useState('');
  const [totalValue, setTotalValue] = useState('');
  const [hideOutOfStockProducts, setHideOutOfStockProducts] = useState(false);
  
  // Opciones para filtros
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [families, setFamilies] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  // Estados para modal de selección de almacén
  const [isWarehouseModalOpen, setIsWarehouseModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  

  useEffect(() => {
    if (!hasLoadedFilters.current) {
      hasLoadedFilters.current = true;
      loadFilterOptions();
    }
  }, []);

  // Recargar reporte al cambiar el almacén (o al cargar almacenes la primera vez)
  useEffect(() => {
    if (!hasLoadedReport.current) {
      hasLoadedReport.current = true;
    }
    loadKardexReport();
  }, [selectedWarehouse, warehouses]);

  useEffect(() => {
    if (selectedCategory) {
      loadSubcategories();
    } else {
      setSubcategories([]);
      setSelectedSubcategory('');
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedSubcategory) {
      loadFamilies();
    } else {
      setFamilies([]);
      setSelectedFamily('');
    }
  }, [selectedSubcategory]);

  useEffect(() => {
    applyFilters();
  }, [
    searchTerm,
    selectedCategory,
    selectedSubcategory,
    selectedFamily,
    selectedWarehouse,
    selectedCondition,
    averageCost,
    totalValue,
    hideOutOfStockProducts,
    allProducts,
  ]);

  const loadKardexReport = async () => {
    const warehouseId = selectedWarehouse && warehouses.length
      ? (warehouses.find(w => w.code === selectedWarehouse)?.id ?? null)
      : null;
    try {
      setLoading(true);
      const response = await reportService.getKardexReport(warehouseId || undefined);
      if (response.success) {
        setAllProducts(response.data || []);
        setProducts(response.data || []);
      } else {
        setToast({
          isOpen: true,
          message: 'Error al cargar el reporte de kardex',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al cargar reporte:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar el reporte de kardex',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const [categoriesRes, warehousesRes] = await Promise.all([
        classificationService.getCategories({ active_only: false }),
        getWarehouses({ active_only: true })
      ]);

      if (categoriesRes.success) {
        setCategories(filterProductCategoriesExcludingRecetaMirror(categoriesRes.data || []));
      }
      if (warehousesRes.success) {
        setWarehouses(
          filterWarehousesForUser(warehousesRes.data || [], authService.getCurrentUser())
        );
      }
    } catch (error) {
      console.error('Error al cargar opciones de filtros:', error);
    }
  };

  const loadSubcategories = async () => {
    try {
      const response = await classificationService.getSubcategories({
        category_id: selectedCategory,
        active_only: false
      });
      if (response.success) {
        setSubcategories(filterProductSubcategoriesExcludingRecetaMirror(response.data || []));
      }
    } catch (error) {
      console.error('Error al cargar subcategorías:', error);
    }
  };

  const loadFamilies = async () => {
    try {
      const response = await classificationService.getFamilies({
        subcategory_id: selectedSubcategory,
        active_only: false
      });
      if (response.success) {
        setFamilies(filterProductFamiliesExcludingRecetaMirror(response.data || []));
      }
    } catch (error) {
      console.error('Error al cargar familias:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...allProducts];

    // Filtro por búsqueda (nombre o SKU)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(product =>
        (product.name && product.name.toLowerCase().includes(search)) ||
        (product.sku && product.sku.toLowerCase().includes(search))
      );
    }

    // Filtro por categoría
    if (selectedCategory) {
      filtered = filtered.filter(product =>
        product.classification?.category &&
        categories.find(cat => cat.id === parseInt(selectedCategory) && cat.name === product.classification.category)
      );
    }

    // Filtro por subcategoría
    if (selectedSubcategory) {
      filtered = filtered.filter(product =>
        product.classification?.subcategory &&
        subcategories.find(sub => sub.id === parseInt(selectedSubcategory) && sub.name === product.classification.subcategory)
      );
    }

    // Filtro por familia
    if (selectedFamily) {
      filtered = filtered.filter(product =>
        product.family &&
        families.find(fam => fam.id === parseInt(selectedFamily) && fam.name === product.family)
      );
    }

    // El filtro por almacén recarga el reporte desde el API (stock acotado al almacén).
    // No se excluyen productos con stock cero aquí; eso lo controla hideOutOfStockProducts.

    // Filtro por condición/calidad
    if (selectedCondition) {
      filtered = filtered.filter(product =>
        hasNonZeroStock(product.stock_by_condition[selectedCondition])
      );
    }

    // Filtro por costo promedio
    if (averageCost !== '') {
      const cost = parseFloat(averageCost);
      filtered = filtered.filter(product => product.average_cost >= cost);
    }

    // Filtro por valor total
    if (totalValue !== '') {
      const value = parseFloat(totalValue);
      filtered = filtered.filter(product => product.total_value >= value);
    }

    // Filtro para ocultar productos sin stock (solo exactamente cero; negativos sí se muestran)
    if (hideOutOfStockProducts) {
      filtered = filtered.filter((product) => hasNonZeroStock(product.total_stock));
    }

    setProducts(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSelectedFamily('');
    setSelectedWarehouse('');
    setSelectedCondition('');
    setAverageCost('');
    setTotalValue('');
    setHideOutOfStockProducts(false);
  };

  const formatDateLocaleLima = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-PE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const exportAllKardexToExcel = async () => {
    try {
      setLoading(true);
      const warehouseId = selectedWarehouse && warehouses.length
        ? (warehouses.find(w => w.code === selectedWarehouse)?.id ?? null)
        : null;
      const exportScopeLabel = warehouseId
        ? `almacén ${selectedWarehouse}`
        : 'todos los almacenes';

      setToast({
        isOpen: true,
        message: `Cargando kardex de ${exportScopeLabel}... Esto puede tomar unos momentos.`,
        type: 'info'
      });

      const response = await reportService.getAllKardexWithMovements(warehouseId || undefined);
      
      if (!response.success) {
        throw new Error(response.message || 'Error al obtener los kardex');
      }

      const allKardexData = response.data || [];
      const endDate = response.end_date;

      if (allKardexData.length === 0) {
        setToast({
          isOpen: true,
          message: 'No hay datos de kardex para exportar',
          type: 'warning'
        });
        setLoading(false);
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Todos los Kardex');

      // Título
      worksheet.mergeCells('A1:S1');
      const titleRow = worksheet.getRow(1);
      titleRow.height = 30;
      titleRow.getCell(1).value = 'TODOS LOS KARDEX - HASTA EL MES ACTUAL';
      titleRow.getCell(1).font = { size: 16, bold: true };
      titleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

      // Información del reporte
      worksheet.mergeCells('A2:S2');
      const infoRow = worksheet.getRow(2);
      infoRow.getCell(1).value = `Todos los movimientos registrados hasta: ${new Date(endDate).toLocaleDateString('es-PE', { dateStyle: 'long' })}`;
      infoRow.getCell(1).font = { size: 12 };
      infoRow.getCell(1).alignment = { horizontal: 'center' };

      // Fecha de exportación
      worksheet.mergeCells('A3:S3');
      const dateRow = worksheet.getRow(3);
      dateRow.getCell(1).value = `Exportado el: ${new Date().toLocaleString('es-PE', { dateStyle: 'long', timeStyle: 'short' })}`;
      dateRow.getCell(1).font = { size: 10, italic: true };
      dateRow.getCell(1).alignment = { horizontal: 'center' };

      // Encabezados de la tabla
      const headerRow = worksheet.getRow(5);
      const plateOffset = machineryEnabled ? 1 : 0;
      const headers = [
        'Producto', 'SKU', 'Almacén', 'Fecha', 'Hora', 'Evento', 'Guía',
        ...(machineryEnabled ? ['Placa'] : []),
        'Entidad', 'Referencia',
        'ENTRADAS - Unidades', 'ENTRADAS - Costo Unit', 'ENTRADAS - Valor Total',
        'SALIDAS - Unidades', 'SALIDAS - Costo Unit', 'SALIDAS - Valor Total',
        'EXISTENCIAS - Unidades', 'EXISTENCIAS - Costo Unit', 'EXISTENCIAS - Valor Total',
        'Condición', 'Revisión'
      ];
      const totalCols = headers.length;

      headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.font = { bold: true, size: 11 };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE0E0E0' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      headerRow.height = 30;

      // Mapeo de eventos
      const eventMap = {
        'compra': 'Compra',
        'venta': 'Venta',
        'saldo_inicial': 'Saldo Inicial',
        'traspaso': 'Traspaso',
        'recepcion': 'Recepción',
        'ajuste_inventario': 'Ajuste inventario',
        'ajuste_costo': 'Ajuste de costo',
        'transferencia_gratuita': 'Transferencia Gratuita',
        'descuento_obtenido': 'Descuento Obtenido',
        'descuento_concedido': 'Descuento Concedido',
        'descuento_con_mercaderia': 'Descuento con Mercadería',
        'consumo': 'Consumo',
        'devolucion': 'Devolución'
      };

      const conditionMap = {
        'good': 'Bueno',
        'damaged': 'Dañado',
        'defective': 'Defectuoso',
        'reusable': 'Reutilizable',
        'non_reusable': 'No reutilizable'
      };

      // Datos
      let currentRow = 6;

      allKardexData.forEach((kardexItem) => {
        const product = kardexItem.product;
        const warehouse = kardexItem.warehouse;
        const movements = kardexItem.movements || [];

        // Clasificación
        let classificationText = '-';
        if (product.classification) {
          const parts = [];
          if (product.classification.category) parts.push(product.classification.category);
          if (product.classification.subcategory) parts.push(product.classification.subcategory);
          if (product.classification.family) parts.push(product.classification.family);
          classificationText = parts.join(' > ') || '-';
        }

        movements.forEach((movement) => {
          const row = worksheet.getRow(currentRow);
          
          // Producto
          row.getCell(1).value = product.name || '-';
          
          // SKU
          row.getCell(2).value = product.sku || '-';
          
          // Almacén
          row.getCell(3).value = `${warehouse.code} - ${warehouse.name}`;
          
          // Fecha
          row.getCell(4).value = formatDateLocaleLima(movement.movement_date);
          
          // Hora
          row.getCell(5).value = movement.movement_time || '-';
          
          // Evento
          const kardexEvent = movement.kardex_event || movement.guide_type || movement.movement_type;
          row.getCell(6).value = eventMap[kardexEvent] || kardexEvent || '-';
          
          // Guía
          row.getCell(7).value = movement.guide_number || movement.entry_guide?.code || '-';

          let col = 8;
          if (machineryEnabled) {
            row.getCell(col).value = movement.vehicle_license_plate || '-';
            col += 1;
          }
          
          // Entidad
          row.getCell(col).value = movement.entity_name || movement.entry_guide?.supplier?.name || '-';
          col += 1;
          
          // Referencia (para devoluciones: guía de salida; si no, reference del backend)
          row.getCell(col).value = (movement.kardex_event === 'devolucion' && movement.consumption_exit_guide_code)
            ? movement.consumption_exit_guide_code
            : (movement.reference || '-');
          col += 1;
          
          // ENTRADAS (devoluciones no se muestran aquí, van a salidas como negativo)
          if (movement.movement_type === 'entrada' && movement.kardex_event !== 'devolucion') {
            row.getCell(col).value = movement.quantity || 0;
            row.getCell(col + 1).value = movement.unit_price || 0;
            row.getCell(col + 2).value = movement.total || 0;
          } else {
            row.getCell(col).value = '-';
            row.getCell(col + 1).value = '-';
            row.getCell(col + 2).value = '-';
          }
          
          // SALIDAS (salidas positivas; devoluciones: solo cantidad y valor total en negativo, costo unitario positivo)
          if (movement.movement_type === 'salida') {
            row.getCell(col + 3).value = movement.quantity || 0;
            row.getCell(col + 4).value = movement.unit_price || 0;
            row.getCell(col + 5).value = movement.total || 0;
          } else if (movement.movement_type === 'entrada' && movement.kardex_event === 'devolucion') {
            row.getCell(col + 3).value = -(movement.quantity || 0);
            row.getCell(col + 4).value = movement.unit_price || 0;
            row.getCell(col + 5).value = -(movement.total || 0);
          } else {
            row.getCell(col + 3).value = '-';
            row.getCell(col + 4).value = '-';
            row.getCell(col + 5).value = '-';
          }
          
          // EXISTENCIAS
          row.getCell(col + 6).value = movement.stock_after || 0;
          row.getCell(col + 7).value = movement.average_cost_after !== 0 && movement.average_cost_after !== null 
            ? movement.average_cost_after 
            : '-';
          row.getCell(col + 8).value = movement.total_value_after || 0;
          
          // Condición
          row.getCell(col + 9).value = conditionMap[movement.condition] || movement.condition || 'Bueno';

          // Revisión (inconsistencias detectadas: costo incorrecto, stock negativo, etc.)
          row.getCell(col + 10).value = movement.revision ?? '-';
          
          // Aplicar bordes
          for (let i = 1; i <= totalCols; i++) {
            row.getCell(i).border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
            row.getCell(i).alignment = { vertical: 'top', wrapText: true };
          }

          // Alineación de números
          [0, 1, 2, 3, 4, 5, 6, 7, 8].forEach((offset) => {
            row.getCell(col + offset).alignment = { horizontal: 'right', vertical: 'top' };
          });
          
          currentRow++;
        });
      });

      // Ajustar ancho de columnas
      worksheet.columns = [
        { width: 30 }, // Producto
        { width: 20 }, // SKU
        { width: 20 }, // Almacén
        { width: 12 }, // Fecha
        { width: 10 }, // Hora
        { width: 20 }, // Evento
        { width: 12 }, // Guía
        ...(machineryEnabled ? [{ width: 12 }] : []), // Placa
        { width: 25 }, // Entidad
        { width: 15 }, // Referencia
        { width: 15 }, // ENTRADAS - Unidades
        { width: 15 }, // ENTRADAS - Costo Unit
        { width: 15 }, // ENTRADAS - Valor Total
        { width: 15 }, // SALIDAS - Unidades
        { width: 15 }, // SALIDAS - Costo Unit
        { width: 15 }, // SALIDAS - Valor Total
        { width: 15 }, // EXISTENCIAS - Unidades
        { width: 15 }, // EXISTENCIAS - Costo Unit
        { width: 15 }, // EXISTENCIAS - Valor Total
        { width: 12 }, // Condición
        { width: 30 }, // Revisión
      ];

      // Formatear números
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 5) {
          // Columnas numéricas base 10-18 (+ placa si aplica)
          [10, 11, 12, 13, 14, 15, 16, 17, 18].forEach((baseCol) => {
            const colNum = baseCol + plateOffset;
            const cell = row.getCell(colNum);
            if (cell.value !== '-' && cell.value !== null && cell.value !== undefined) {
              if (baseCol === 11 || baseCol === 14 || baseCol === 17) {
                cell.numFmt = '#,##0.0000';
              } else if (baseCol === 12 || baseCol === 15 || baseCol === 18) {
                cell.numFmt = '#,##0.0000';
              } else {
                cell.numFmt = '#,##0.000';
              }
            }
          });
        }
      });

      // Congelar paneles (fila de encabezados)
      worksheet.views = [{
        state: 'frozen',
        ySplit: 5
      }];

      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Todos_Los_Kardex_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setToast({
        isOpen: true,
        message: `Todos los kardex exportados exitosamente (${allKardexData.length} productos-almacenes)`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error al exportar todos los kardex:', error);
      setToast({
        isOpen: true,
        message: 'Error al exportar todos los kardex: ' + (error.message || 'Error desconocido'),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Reporte de Kardex');

      // Estilos
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF1F2937' }
        },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const titleStyle = {
        font: { bold: true, size: 16 },
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      // Título
      worksheet.mergeCells('A1:K1');
      worksheet.getCell('A1').value = 'Reporte de Kardex';
      worksheet.getCell('A1').style = titleStyle;
      worksheet.getRow(1).height = 25;

      // Fecha de exportación
      worksheet.mergeCells('A2:K2');
      worksheet.getCell('A2').value = `Fecha de exportación: ${new Date().toLocaleString('es-PE')}`;
      worksheet.getCell('A2').style = { alignment: { horizontal: 'center' }, font: { italic: true } };
      worksheet.getRow(2).height = 20;

      // Encabezados
      const headers = [
        'Producto',
        'SKU',
        'Categoría',
        'Subcategoría',
        'Familia',
        'Stock Total',
        'Unidad',
        'Stock por Calidad',
        'Stock por Almacén',
        'Costo Promedio',
        'Valor Total'
      ];

      worksheet.addRow(headers);
      const headerRow = worksheet.getRow(3);
      headerRow.height = 25;
      headerRow.eachCell((cell, colNumber) => {
        cell.style = headerStyle;
      });

      // Datos
      products.forEach((product) => {
        const category = product.classification?.category || '-';
        const subcategory = product.classification?.subcategory || '-';
        const family = product.classification?.family || product.family || '-';

        // Stock por calidad
        const stockByCondition = [];
        Object.entries(product.stock_by_condition).forEach(([condition, qty]) => {
          if (hasNonZeroStock(qty)) {
            stockByCondition.push(`${getConditionLabel(condition)}: ${Number(qty).toFixed(3)}`);
          }
        });
        const stockConditionText = stockByCondition.length > 0 
          ? stockByCondition.join('\n') 
          : 'Sin stock';

        // Stock por almacén
        const stockByWarehouse = product.stock_by_warehouse
          .filter(wh => hasNonZeroStock(wh.stock))
          .map(wh => `${wh.warehouse_code}${wh.warehouse_name ? ` (${wh.warehouse_name})` : ''}: ${wh.stock.toFixed(3)}`)
          .join('\n') || 'Sin stock';

        const row = worksheet.addRow([
          product.name || '-',
          product.sku || '-',
          category,
          subcategory,
          family,
          Number(product.total_stock.toFixed(3)),
          product.unit_of_measure || '-',
          stockConditionText,
          stockByWarehouse,
          product.average_cost > 0 ? `S/ ${product.average_cost.toFixed(4)}` : '-',
          product.total_value > 0 ? `S/ ${product.total_value.toFixed(4)}` : '-'
        ]);

        // Estilos para la fila
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { vertical: 'top', wrapText: true };
        });

        // Alineación específica
        row.getCell(6).alignment = { horizontal: 'right', vertical: 'top' }; // Stock Total
        row.getCell(6).numFmt = '0.000';
        row.getCell(7).alignment = { horizontal: 'center', vertical: 'top' }; // Unidad
        row.getCell(10).alignment = { horizontal: 'right', vertical: 'top' }; // Costo Promedio
        row.getCell(11).alignment = { horizontal: 'right', vertical: 'top' }; // Valor Total
      });

      // Ajustar ancho de columnas
      worksheet.columns = [
        { width: 30 }, // Producto
        { width: 20 }, // SKU
        { width: 22 }, // Categoría
        { width: 22 }, // Subcategoría
        { width: 22 }, // Familia
        { width: 14 }, // Stock Total
        { width: 10 }, // Unidad
        { width: 25 }, // Stock por Calidad
        { width: 30 }, // Stock por Almacén
        { width: 18 }, // Costo Promedio
        { width: 18 }  // Valor Total
      ];

      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Reporte_Kardex_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setToast({
        isOpen: true,
        message: 'Reporte exportado exitosamente',
        type: 'success'
      });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      setToast({
        isOpen: true,
        message: 'Error al exportar el reporte',
        type: 'error'
      });
    }
  };

  const getConditionLabel = (condition) => {
    const labels = {
      'good': 'Bueno',
      'damaged': 'Dañado',
      'defective': 'Defectuoso',
      'reusable': 'Reutilizable',
      'non_reusable': 'No reutilizable'
    };
    return labels[condition] || condition;
  };

  const getConditionColor = (condition) => {
    const colors = {
      'good': 'bg-green-100 text-green-800',
      'damaged': 'bg-yellow-100 text-yellow-800',
      'defective': 'bg-red-100 text-red-800',
      'reusable': 'bg-blue-100 text-blue-800',
      'non_reusable': 'bg-purple-100 text-purple-800'
    };
    return colors[condition] || 'bg-gray-100 text-gray-800';
  };

  const isRecetaProduct = (product) => product?.is_receta_terminado === true;

  const insumoProducts = products.filter((product) => !isRecetaProduct(product));
  const recetaProducts = products.filter((product) => isRecetaProduct(product));

  const renderProductRow = (product) => (
    <tr key={product.id} className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">{product.name}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-500">{product.sku || '-'}</div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-gray-500">
          {product.classification ? (
            <div className="flex flex-col">
              {product.classification.category && (
                <span className="font-medium">{product.classification.category}</span>
              )}
              {product.classification.subcategory && (
                <span className="text-xs text-gray-400">
                  {product.classification.category && ' > '}
                  {product.classification.subcategory}
                </span>
              )}
              {product.classification.family && (
                <span className="text-xs text-gray-400">
                  {(product.classification.category || product.classification.subcategory) && ' > '}
                  {product.classification.family}
                </span>
              )}
            </div>
          ) : (
            <span>{product.family || '-'}</span>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className={`text-sm ${stockQtyClassName(product.total_stock)}`}>
          {formatStockQty(product.total_stock)} {product.unit_of_measure || ''}
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="flex flex-col gap-1">
          {Object.entries(product.stock_by_condition).map(([condition, quantity]) => (
            hasNonZeroStock(quantity) && (
              <span
                key={condition}
                className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getConditionColor(condition)} ${Number(quantity) < 0 ? 'ring-1 ring-red-300' : ''}`}
              >
                {getConditionLabel(condition)}: {formatStockQty(quantity)}
              </span>
            )
          ))}
          {Object.values(product.stock_by_condition).every((qty) => !hasNonZeroStock(qty)) && (
            <span className="text-xs text-gray-400">Sin stock</span>
          )}
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="flex flex-col gap-1">
          {product.stock_by_warehouse.map((warehouse) => (
            hasNonZeroStock(warehouse.stock) && (
              <div key={warehouse.warehouse_code} className={`text-xs ${Number(warehouse.stock) < 0 ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                <span className="font-medium">{warehouse.warehouse_code}</span>
                {warehouse.warehouse_name && (
                  <span className="text-xs text-gray-400 ml-1">({warehouse.warehouse_name})</span>
                )}
                <span className="ml-1">
                  : {formatStockQty(warehouse.stock)}
                </span>
              </div>
            )
          ))}
          {product.stock_by_warehouse.every((w) => !hasNonZeroStock(w.stock)) && (
            <span className="text-xs text-gray-400">Sin stock</span>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">
          {product.average_cost > 0
            ? `S/ ${product.average_cost.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`
            : '-'}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className={`text-sm font-medium ${Number(product.total_value) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
          {Math.abs(Number(product.total_value) || 0) > STOCK_ZERO_EPS
            ? `S/ ${Number(product.total_value).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`
            : '-'}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <button
          onClick={() => {
            setSelectedProduct(product);
            setIsWarehouseModalOpen(true);
          }}
          className="text-teal-600 hover:text-teal-900 font-medium"
        >
          Detalle
        </button>
      </td>
    </tr>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Reporte de Kardex</h1>
            <p className="text-gray-600 mt-1">Revisa todos los productos y sus movimientos en el kardex</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={exportToExcel}
              disabled={loading || products.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Exportar a Excel
            </button>
            <button
              onClick={exportAllKardexToExcel}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Exportar Todos los Kardex
            </button>
          </div>
        </div>
      </div>

      {/* Panel de Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Filtros</h2>
          <button
            onClick={clearFilters}
            className="text-sm text-teal-600 hover:text-teal-700 font-medium"
          >
            Limpiar filtros
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {/* Búsqueda */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar (Producto/SKU)
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nombre o SKU..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          {/* Categoría */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Categoría
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Todas</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Subcategoría */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subcategoría
            </label>
            <select
              value={selectedSubcategory}
              onChange={(e) => setSelectedSubcategory(e.target.value)}
              disabled={!selectedCategory}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
            >
              <option value="">Todas</option>
              {subcategories.map(sub => (
                <option key={sub.id} value={sub.id}>{sub.name}</option>
              ))}
            </select>
          </div>

          {/* Familia */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Familia
            </label>
            <select
              value={selectedFamily}
              onChange={(e) => setSelectedFamily(e.target.value)}
              disabled={!selectedSubcategory}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
            >
              <option value="">Todas</option>
              {families.map(fam => (
                <option key={fam.id} value={fam.id}>{fam.name}</option>
              ))}
            </select>
          </div>

          {/* Almacén */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Almacén
            </label>
            <select
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Todos</option>
              {warehouses.map(wh => (
                <option key={wh.code} value={wh.code}>{wh.code} - {wh.name}</option>
              ))}
            </select>
          </div>

          {/* Condición/Calidad */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Calidad
            </label>
            <select
              value={selectedCondition}
              onChange={(e) => setSelectedCondition(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Todas</option>
              <option value="good">Bueno</option>
              <option value="damaged">Dañado</option>
              <option value="defective">Defectuoso</option>
              <option value="reusable">Reutilizable</option>
              <option value="non_reusable">No reutilizable</option>
            </select>
          </div>

          {/* Costo Promedio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Costo Promedio (≥)
            </label>
            <input
              type="number"
              step="0.01"
              value={averageCost}
              onChange={(e) => setAverageCost(e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          {/* Valor Total */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valor Total (≥)
            </label>
            <input
              type="number"
              step="0.01"
              value={totalValue}
              onChange={(e) => setTotalValue(e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          {/* Ocultar productos sin stock */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stock
            </label>
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg w-full">
              <input
                type="checkbox"
                checked={hideOutOfStockProducts}
                onChange={(e) => setHideOutOfStockProducts(e.target.checked)}
                className="h-4 w-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">
                Ocultar productos con stock cero
              </span>
            </label>
          </div>
        </div>

        {/* Contador de resultados */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Mostrando <span className="font-semibold">{products.length}</span> de <span className="font-semibold">{allProducts.length}</span> productos
          </p>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            <p className="mt-4 text-gray-600">Cargando reporte...</p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SKU
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Familia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock por Calidad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock por Almacén
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Costo Promedio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                      No hay productos con movimientos en el kardex
                    </td>
                  </tr>
                ) : (
                  showRecipeSegmentation ? (
                    <>
                      {insumoProducts.length > 0 && (
                        <tr className="bg-teal-50">
                          <td colSpan="9" className="px-6 py-2 text-xs font-semibold uppercase tracking-wider text-teal-800">
                            Insumos ({insumoProducts.length})
                          </td>
                        </tr>
                      )}
                      {insumoProducts.map(renderProductRow)}

                      {recetaProducts.length > 0 && (
                        <tr className="bg-indigo-50">
                          <td colSpan="9" className="px-6 py-2 text-xs font-semibold uppercase tracking-wider text-indigo-800">
                            Recetas / Productos terminados ({recetaProducts.length})
                          </td>
                        </tr>
                      )}
                      {recetaProducts.map(renderProductRow)}
                    </>
                  ) : (
                    products.map(renderProductRow)
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal para seleccionar almacén */}
      {isWarehouseModalOpen && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">Seleccionar Almacén</h3>
                <button
                  onClick={() => {
                    setIsWarehouseModalOpen(false);
                    setSelectedProduct(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Producto: <span className="font-medium">{selectedProduct.name}</span>
              </p>
            </div>
            <div className="p-6">
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {warehouses.length > 0 ? (
                  warehouses.map((warehouse) => {
                    const stockInfo = selectedProduct.stock_by_warehouse.find(wh => wh.warehouse_code === warehouse.code);
                    const stock = stockInfo ? stockInfo.stock : 0;
                    
                    return (
                      <button
                        key={warehouse.id}
                        onClick={() => {
                          setIsWarehouseModalOpen(false);
                          navigate(`/reports/kardex/${selectedProduct.id}/${warehouse.id}`);
                        }}
                        className="w-full p-4 border border-gray-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 cursor-pointer transition-colors text-left"
                      >
                        <div className="font-medium text-gray-900">{warehouse.code}</div>
                        {warehouse.name && (
                          <div className="text-sm text-gray-600">{warehouse.name}</div>
                        )}
                        <div className={`text-sm mt-1 ${stockQtyClassName(stock)}`}>
                          Stock: {formatStockQty(stock)}
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <p className="text-center text-gray-500 py-4">No hay almacenes disponibles</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default Kardex;

